import { mergeAttributes, type AnyExtension } from '@tiptap/core';
import HardBreak from '@tiptap/extension-hard-break';
import Link from '@tiptap/extension-link';
import Mention, { type MentionOptions } from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import type { Mark, Node as ProseMirrorNode } from '@tiptap/pm/model';
import {
  Markdown,
  type MarkdownMarkSpec,
  type MarkdownNodeSpec,
} from 'tiptap-markdown';
import rendererStyles from './PostBodyRenderer.module.css';

// TipTap extension set for the member-facing rich text editors (post
// composer, comment composer, event-form RichTextArea). The document
// serializes to the markdown dialect decided in
// docs/design/decisions/2026-08-04-post-editor-rework.md:
//
//   **bold**  *italic*  <u>underline</u>  - bullet  1. ordered
//   [text](url) links (bare URLs stay bare)  @handle mentions
//   hard break = single \n  paragraph break = blank line
//
// Everything else (headings, quotes, code, strike, tables, images) is
// disabled so the editor cannot produce shapes outside the dialect, and
// literal markdown metacharacters in prose are escaped by the serializer.

type MarkdownSerializerState = Parameters<MarkdownNodeSpec['serialize']>[0];

// tiptap-markdown's default hardBreak spec emits "\\\n" (backslash hard
// break). The dialect stores a bare "\n" — the read side uses remark-breaks
// and the parse side runs markdown-it with `breaks: true`, so a plain
// newline round-trips — and a backslash would pollute search_text and
// body previews. Trailing hard breaks are skipped (same as the default):
// a paragraph's own closing blank line already encodes them ambiguously.
const RichTextHardBreak = HardBreak.extend({
  addStorage() {
    const markdown: MarkdownNodeSpec = {
      serialize(state, node, parent, index) {
        for (let i = index + 1; i < parent.childCount; i++) {
          if (parent.child(i).type !== node.type) {
            state.write('\n');
            return;
          }
        }
      },
      parse: {},
    };
    return { markdown };
  },
});

// Mirrors prosemirror-markdown's isPlainURL: the link's rendered text is
// exactly its href, so the bare URL alone re-parses to the same link.
function isPlainUrl(link: Mark, parent: ProseMirrorNode, index: number): boolean {
  if (typeof link.attrs['href'] !== 'string' || !/^\w+:/.test(link.attrs['href'])) {
    return false;
  }
  const content = parent.child(index);
  if (
    !content.isText ||
    content.text !== link.attrs['href'] ||
    content.marks[content.marks.length - 1] !== link
  ) {
    return false;
  }
  return (
    index === parent.childCount - 1 ||
    !link.isInSet(parent.child(index + 1).marks)
  );
}

// prosemirror-markdown's default link spec serializes a plain URL as an
// `<url>` autolink. The dialect keeps bare URLs bare — `linkify: true` on
// the parse side re-links them — so stored bodies, search_text, and
// notification previews carry the clean URL, matching what members typed.
// `inAutolink` piggybacks on prosemirror-markdown's own escape suppression
// so URL underscores don't get backslash-escaped.
const RichTextLink = Link.extend({
  addStorage() {
    const markdown: MarkdownMarkSpec = {
      serialize: {
        open(state: MarkdownSerializerState & { inAutolink?: boolean }, mark: Mark, parent: ProseMirrorNode, index: number) {
          state.inAutolink = isPlainUrl(mark, parent, index);
          return state.inAutolink ? '' : '[';
        },
        close(state: MarkdownSerializerState & { inAutolink?: boolean }, mark: Mark) {
          const { inAutolink } = state;
          delete state.inAutolink;
          return inAutolink === true
            ? ''
            : `](${String(mark.attrs['href']).replace(/[()"]/g, '\\$&')})`;
        },
        mixable: true,
      },
      parse: {},
    };
    return { markdown };
  },
});

export type MentionNames = ReadonlyMap<string, string>;

type RichTextMentionOptions = MentionOptions & {
  /** handle → display name; `@handle` tokens with an entry hydrate into
   *  chips when a markdown value is loaded, others stay literal text.
   *  Callers pass a ref-stable (mutable) Map they grow as mentions are
   *  picked; the contents are read at parse time. */
  mentionNames: MentionNames | null;
};

// Matches the shared mention pattern (`(?<![a-zA-Z0-9.])@([a-z0-9.]{1,40})\b`)
// against the text starting after an `@`; the walk below checks the
// lookbehind. `\b` backtracking trims trailing dots the same way the
// renderer/server regex does.
const MENTION_AT_START = /^[a-z0-9.]{1,40}\b/;

function mentionChipSpan(
  doc: Document,
  handle: string,
  name: string,
): HTMLSpanElement {
  const span = doc.createElement('span');
  span.setAttribute('data-type', 'mention');
  span.setAttribute('data-id', handle);
  span.setAttribute('data-label', name);
  return span;
}

function chipifyTextNode(node: Text, names: MentionNames): void {
  const text = node.textContent ?? '';
  const doc = node.ownerDocument;
  const parts: Array<string | HTMLSpanElement> = [];
  let plainFrom = 0;
  let i = 0;
  while (i < text.length) {
    if (
      text[i] === '@' &&
      (i === 0 || !/[a-zA-Z0-9.]/.test(text[i - 1]!))
    ) {
      const match = MENTION_AT_START.exec(text.slice(i + 1));
      const name = match !== null ? names.get(match[0]) : undefined;
      if (match !== null && name !== undefined) {
        parts.push(text.slice(plainFrom, i));
        parts.push(mentionChipSpan(doc, match[0], name));
        i += 1 + match[0].length;
        plainFrom = i;
        continue;
      }
    }
    i++;
  }
  if (plainFrom === 0) return;
  parts.push(text.slice(plainFrom));
  const parent = node.parentNode;
  if (parent === null) return;
  for (const part of parts) {
    if (typeof part === 'string') {
      if (part.length > 0) parent.insertBefore(doc.createTextNode(part), node);
    } else {
      parent.insertBefore(part, node);
    }
  }
  parent.removeChild(node);
}

function chipifyMentions(root: HTMLElement, names: MentionNames): void {
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      chipifyTextNode(child as Text, names);
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const el = child as HTMLElement;
    if (el.tagName === 'A' || el.getAttribute('data-type') === 'mention') {
      continue;
    }
    chipifyMentions(el, names);
  }
}

const RichTextMention = Mention.extend<RichTextMentionOptions>({
  addOptions() {
    return {
      ...(this.parent?.() as MentionOptions),
      mentionNames: null,
      renderText({ node }) {
        return `@${node.attrs['id']}`;
      },
      renderHTML({ options, node }) {
        return [
          'span',
          mergeAttributes(options.HTMLAttributes, {
            class: rendererStyles['mention'],
          }),
          String(node.attrs['label'] ?? `@${node.attrs['id']}`),
        ];
      },
    };
  },
  addStorage() {
    const markdown: MarkdownNodeSpec<RichTextMentionOptions> = {
      serialize(state, node) {
        state.write(`@${node.attrs['id']}`);
      },
      parse: {
        updateDOM(element) {
          const names = this.options.mentionNames;
          if (names !== null && names.size > 0) {
            chipifyMentions(element, names);
          }
        },
      },
    };
    return { markdown };
  },
});

export type RichTextExtensionsOptions = {
  readonly placeholder?: string;
  readonly mentionNames?: MentionNames;
  /** Suggestion-popup wiring (trigger char, command, render lifecycle).
   *  Provided by RichTextEditor; omitted in headless/serialization use. */
  readonly mentionSuggestion?: MentionOptions['suggestion'];
};

export function richTextExtensions(
  options: RichTextExtensionsOptions = {},
): AnyExtension[] {
  return [
    StarterKit.configure({
      heading: false,
      code: false,
      codeBlock: false,
      blockquote: false,
      horizontalRule: false,
      strike: false,
      hardBreak: false,
    }),
    RichTextHardBreak,
    Underline,
    RichTextLink.configure({
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
      isAllowedUri: (url, ctx) =>
        ctx.defaultValidate(url) && /^https?:\/\//i.test(url),
    }),
    RichTextMention.configure({
      mentionNames: options.mentionNames ?? null,
      ...(options.mentionSuggestion !== undefined
        ? { suggestion: options.mentionSuggestion }
        : {}),
    }),
    Placeholder.configure({ placeholder: options.placeholder ?? '' }),
    Markdown.configure({
      html: true,
      breaks: true,
      linkify: true,
      tightLists: true,
      bulletListMarker: '-',
    }),
  ];
}

export function editorMarkdown(editor: {
  storage: Record<string, unknown>;
}): string {
  return (editor.storage['markdown'] as { getMarkdown: () => string }).getMarkdown();
}
